import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, Search, Archive, Mic, FileText, Map, Store, Mail, ArrowRight, Loader2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface LibraryBook {
  category?: string;
  genre?: string;
  author?: string;
  referenceNo?: string;
  copies?: string;
  title?: string;
  publisher?: string;
  publicationPlace?: string;
  coverUrl?: string;
  isbn?: string;
  description?: string;
  publicationYear?: string;
}

interface SearchResults {
  results: LibraryBook[];
  total: number;
  query?: string;
}

const LibraryLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/library-search?q=${encodeURIComponent(searchQuery.trim())}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to search library catalogue');
      }
      
      const data: SearchResults = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Library search error:', error);
      setSearchError('Failed to search the library. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchError(null);
  };

  // Reorganized resources in the specified order
  const allResources = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Publications",
      description: "Access our peer-reviewed journals, research publications, and academic outputs from Highland Institute scholars.",
      buttons: [
        { label: "Browse Journals", href: "https://journals.highlanderpress.org/", external: true },
        { label: "Research Publications", href: "/research", external: false }
      ]
    },
    {
      icon: <Store className="w-8 h-8" />,
      title: "Highlander Press Store",
      description: "Explore and purchase books, monographs, and special collections published by Highlander Press.",
      buttons: [
        { label: "Visit Store", href: "https://highlanderpress.myshopify.com/", external: true }
      ]
    },
    {
      icon: <Archive className="w-8 h-8" />,
      title: "Research Archives",
      description: "Historical documents, field notes, and archival materials from decades of research in Highland Asia.",
      buttons: [
        { label: "Explore Archives", href: "/library/archives", external: false }
      ]
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Oral History Collections",
      description: "Audio recordings and transcriptions documenting oral traditions and community narratives.",
      buttons: [
        { label: "Listen to Collections", href: "/library/oral-history", external: false }
      ]
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Theses & Dissertations",
      description: "Student research projects and dissertations from our PGCertR and fellowship programmes.",
      buttons: [
        { label: "Browse Theses", href: "/library/theses", external: false }
      ]
    },
    {
      icon: <Map className="w-8 h-8" />,
      title: "Finding Aids & Guides",
      description: "Research guides, bibliographies, and navigation tools for exploring our collections.",
      buttons: [
        { label: "View Guides", href: "/library/guides", external: false }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Tier 1: Search Hero Section */}
      <div className="bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Library & Publications
            </h1>
            <p className="text-xl text-green-100 mb-8">
              Search our collections, journals, and archives from Highland Asia and beyond
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title, author, keyword, or subject…"
                  className="block w-full pl-12 pr-4 py-4 text-gray-900 placeholder-gray-500 border-0 rounded-lg focus:ring-2 focus:ring-white focus:outline-none text-lg shadow-xl"
                  aria-label="Search library catalogue"
                />
                <button
                  type="submit"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <span className="sr-only">Search</span>
                  <div className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors mr-1">
                    Search
                  </div>
                </button>
              </div>
            </form>

            {/* Optional: Quick Links */}
            <div className="mt-6 flex flex-wrap justify-center gap-3 text-sm">
              <a href="#catalogue" className="text-green-100 hover:text-white underline">
                Catalogue
              </a>
              <span className="text-green-300">•</span>
              <a href="#resources" className="text-green-100 hover:text-white underline">
                Journals
              </a>
              <span className="text-green-300">•</span>
              <a href="#resources" className="text-green-100 hover:text-white underline">
                Archives
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Section */}
      {(searchResults || isSearching || searchError) && (
        <section className="bg-white py-12 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {isSearching ? 'Searching...' : 'Search Results'}
                  </h2>
                  {searchResults && (
                    <p className="text-gray-600 mt-1">
                      Found {searchResults.total} {searchResults.total === 1 ? 'item' : 'items'}
                      {searchResults.query && ` for "${searchResults.query}"`}
                    </p>
                  )}
                </div>
                <Button 
                  onClick={clearSearch}
                  variant="outline"
                  size="sm"
                  className="text-gray-600 hover:text-gray-900"
                >
                  Clear Search
                </Button>
              </div>

              {/* Loading State */}
              {isSearching && (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                  <span className="ml-3 text-gray-600">Searching library catalogue...</span>
                </div>
              )}

              {/* Error State */}
              {searchError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                  <p className="text-red-600">{searchError}</p>
                </div>
              )}

              {/* Results */}
              {searchResults && !isSearching && (
                <div>
                  {searchResults.results.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
                      <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 text-lg">
                        No matching items found in the catalogue.
                      </p>
                      <p className="text-gray-500 text-sm mt-2">
                        Try adjusting your search terms or browse our collections below.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {searchResults.results.map((book, index) => (
                        <Card key={index} className="hover:shadow-lg transition-shadow">
                          <CardContent className="p-6">
                            <div className="flex gap-6">
                              {/* Book Cover */}
                              {book.coverUrl ? (
                                <img
                                  src={book.coverUrl}
                                  alt={book.title || 'Book cover'}
                                  className="w-24 h-32 object-cover rounded shadow-md flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div className="w-24 h-32 bg-gradient-to-br from-green-100 to-green-50 rounded shadow-md flex items-center justify-center flex-shrink-0">
                                  <BookOpen className="w-8 h-8 text-green-600" />
                                </div>
                              )}

                              {/* Book Details */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                  {book.title || 'Untitled'}
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                  {book.author && (
                                    <div>
                                      <span className="font-medium text-gray-700">Author:</span>
                                      <span className="text-gray-600 ml-2">{book.author}</span>
                                    </div>
                                  )}
                                  
                                  {book.publisher && (
                                    <div>
                                      <span className="font-medium text-gray-700">Publisher:</span>
                                      <span className="text-gray-600 ml-2">{book.publisher}</span>
                                    </div>
                                  )}
                                  
                                  {book.publicationYear && (
                                    <div>
                                      <span className="font-medium text-gray-700">Year:</span>
                                      <span className="text-gray-600 ml-2">{book.publicationYear}</span>
                                    </div>
                                  )}
                                  
                                  {book.publicationPlace && (
                                    <div>
                                      <span className="font-medium text-gray-700">Place:</span>
                                      <span className="text-gray-600 ml-2">{book.publicationPlace}</span>
                                    </div>
                                  )}
                                  
                                  {book.genre && (
                                    <div>
                                      <span className="font-medium text-gray-700">Genre:</span>
                                      <span className="text-gray-600 ml-2">{book.genre}</span>
                                    </div>
                                  )}
                                  
                                  {book.category && (
                                    <div>
                                      <span className="font-medium text-gray-700">Category:</span>
                                      <span className="text-gray-600 ml-2">{book.category}</span>
                                    </div>
                                  )}
                                  
                                  {book.referenceNo && (
                                    <div>
                                      <span className="font-medium text-gray-700">Reference No:</span>
                                      <span className="text-gray-600 ml-2">{book.referenceNo}</span>
                                    </div>
                                  )}
                                  
                                  {book.copies && (
                                    <div>
                                      <span className="font-medium text-gray-700">Copies:</span>
                                      <span className="text-gray-600 ml-2">{book.copies}</span>
                                    </div>
                                  )}
                                  
                                  {book.isbn && (
                                    <div>
                                      <span className="font-medium text-gray-700">ISBN:</span>
                                      <span className="text-gray-600 ml-2">{book.isbn}</span>
                                    </div>
                                  )}
                                </div>

                                {book.description && (
                                  <p className="text-gray-600 text-sm mt-3 line-clamp-2">
                                    {book.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Tier 2: Resource Overview Grid */}
      <section id="resources" className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">
            Explore Our Collections
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Access publications, archives, and research materials from The Highland Institute
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allResources.map((resource, idx) => (
              <Card 
                key={idx} 
                className="h-full hover:shadow-xl transition-all duration-300 hover:border-green-500 flex flex-col"
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-green-50 rounded-lg flex items-center justify-center mb-3 text-green-600">
                    {resource.icon}
                  </div>
                  <CardTitle className="text-xl mb-2">{resource.title}</CardTitle>
                  <CardDescription className="text-sm leading-relaxed line-clamp-3">
                    {resource.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto">
                  <div className="flex flex-col gap-2">
                    {resource.buttons.map((btn, btnIdx) => (
                      btn.external ? (
                        <a
                          key={btnIdx}
                          href={btn.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors text-sm font-medium"
                        >
                          {btn.label}
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      ) : (
                        <Link key={btnIdx} to={btn.href}>
                          <Button className="w-full bg-green-600 hover:bg-green-700 text-sm">
                            {btn.label}
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                      )
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tier 3: Institutional Context */}
      
      {/* About Highlander Press */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-green-50 to-white rounded-lg border border-green-200 p-8 md:p-10">
            <div className="flex items-start gap-6">
              <div className="hidden md:block">
                <div className="w-20 h-20 bg-green-600 rounded-lg flex items-center justify-center text-white">
                  <BookOpen className="w-10 h-10" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  About Highlander Press
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Highlander Press is the publishing arm of The Highland Institute, dedicated to producing 
                  peer-reviewed journals, monographs, and edited volumes that advance scholarship on Highland 
                  Asia. Our publications reflect interdisciplinary research in anthropology, environmental 
                  humanities, indigenous studies, and related fields.
                </p>
                <a
                  href="mailto:publishing@highlandinstitute.org"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors font-medium"
                >
                  <Mail className="w-5 h-5" />
                  Contact Publishing Team
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Research CTA Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Discover Our Research
            </h2>
            <p className="text-xl text-green-100 mb-8">
              Discover our research projects or search the library catalogue
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/research">
                <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-lg">
                  <BookOpen className="mr-2 w-5 h-5" />
                  View Research Projects
                </Button>
              </Link>
              <Link to="/library/catalogue">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg font-semibold shadow-lg transition-all"
                >
                  <Search className="mr-2 w-5 h-5" />
                  Search the Catalogue
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LibraryLandingPage;
